import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api, useAction, useApi } from '../lib/api';
import { date, money, parseAmount, pct, period, titleCase, todayIso } from '../lib/format';
import { useCustomers, useProducts } from '../lib/lookups';
import { useSession } from '../lib/session';
import type { Contract, ContractItem, Invoice } from '../lib/types';
import { BarChart, Legend } from '../components/charts';
import { Icons } from '../components/icons';
import { NEW_PRODUCT, NewProductModal, patternLabel } from './Products';
import { Badge, Button, ButtonLink, Card, Empty, ErrorNote, Field, Input, Modal, MoneyInput, PageHeader, Query, Select, StatusBadge, Stat, Table, Td, Th, Toggle, Tr } from '../components/ui';
import { InvoiceStatus } from './Invoices';

type ScheduleLine = { id: string; date: string; period: string; amount: number; status: string; contractItemId: string | null; journalEntryId: string | null };
type ContractDetail = { contract: Contract; items: ContractItem[]; schedule: ScheduleLine[]; invoices: Invoice[] };

export function ContractsPage() {
  const navigate = useNavigate();
  const customers = useCustomers();
  const { can } = useSession();
  const [status, setStatus] = useState('');
  const q = useApi<Contract[]>(`/api/contracts${status ? `?status=${status}` : ''}`);
  return (
    <>
      <PageHeader
        title="Contracts"
        description="Multi-element arrangements under ASC 606: the price is allocated across items by standalone selling price, then recognized on each item's pattern."
        actions={
          <>
            <Select className="w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {['ACTIVE', 'DRAFT', 'COMPLETED', 'TERMINATED'].map((s) => (
                <option key={s} value={s}>
                  {titleCase(s)}
                </option>
              ))}
            </Select>
            {can('ACCOUNTANT') && (
              <ButtonLink to="/ar/contracts/new" variant="primary" icon={<Icons.Plus size={15} />}>
                New contract
              </ButtonLink>
            )}
          </>
        }
      />
      <Card pad={false}>
        <Query q={q}>
          {(rows) =>
            rows.length === 0 ? (
              <Empty title={status ? 'No contracts with this status' : 'No contracts yet'}>
                {!status && 'A contract sets the price, the term and how often you bill. Activating it builds the revenue schedule and the invoices.'}
              </Empty>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Number</Th>
                    <Th>Customer</Th>
                    <Th>Term</Th>
                    <Th>Billing</Th>
                    <Th right>Value</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <Tr key={c.id} onClick={() => navigate(`/ar/contracts/${c.id}`)}>
                      <Td className="font-medium">{c.number}</Td>
                      <Td>{customers.byId.get(c.customerId)?.name}</Td>
                      <Td className="num whitespace-nowrap">
                        {date(c.startDate)} – {date(c.endDate)}
                      </Td>
                      <Td muted>{titleCase(c.billingFrequency)}</Td>
                      <Td right>{money(c.totalAmount, c.currency)}</Td>
                      <Td>
                        <StatusBadge status={c.status} />
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )
          }
        </Query>
      </Card>
    </>
  );
}

export function ContractPage() {
  const { id } = useParams();
  const { can } = useSession();
  const customers = useCustomers();
  const products = useProducts();
  const q = useApi<ContractDetail>(`/api/contracts/${id}`);
  const [terminating, setTerminating] = useState(false);
  const [termDate, setTermDate] = useState(todayIso());
  const activate = useAction(() => api(`/api/contracts/${id}/activate`, { method: 'POST' }), { success: 'Contract activated: schedules and billing created' });
  const terminate = useAction(() => api(`/api/contracts/${id}/terminate`, { body: { date: termDate } }), { success: 'Contract terminated; future revenue and billing cancelled' });

  return (
    <Query q={q}>
      {({ contract: c, items, schedule, invoices }) => {
        const periods = [...new Set(schedule.map((s) => s.period))].sort();
        const posted = periods.map((p) => schedule.filter((s) => s.period === p && s.status === 'POSTED').reduce((a, s) => a + s.amount, 0));
        const planned = periods.map((p) => schedule.filter((s) => s.period === p && s.status === 'PLANNED').reduce((a, s) => a + s.amount, 0));
        const recognized = posted.reduce((a, b) => a + b, 0);
        const billed = invoices.filter((i) => i.status !== 'DRAFT' && i.status !== 'VOID').reduce((a, i) => a + i.total, 0);
        const customer = customers.byId.get(c.customerId);
        return (
          <>
            <PageHeader
              eyebrow={
                <Link to="/ar/contracts" className="hover:text-ink">
                  ← Contracts
                </Link>
              }
              title={
                <span className="flex items-center gap-3">
                  {c.number} <StatusBadge status={c.status} />
                </span>
              }
              description={customer ? `${customer.name} · ${date(c.startDate)} – ${date(c.endDate)} · billed ${c.billingFrequency.toLowerCase()}` : undefined}
              actions={
                can('ACCOUNTANT') && (
                  <>
                    {c.status === 'DRAFT' && (
                      <Button variant="primary" loading={activate.isPending} onClick={() => activate.mutate()}>
                        Activate
                      </Button>
                    )}
                    {c.status === 'ACTIVE' && (
                      <Button variant="danger" onClick={() => setTerminating(true)}>
                        Terminate
                      </Button>
                    )}
                  </>
                )
              }
            />
            <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat label="Contract value" value={money(c.totalAmount, c.currency)} />
              <Stat label="Recognized" value={money(recognized, c.currency)} sub={`${pct(c.totalAmount ? recognized / c.totalAmount : 0, 0)} of value`} />
              <Stat label="Billed" value={money(billed, c.currency)} sub={`${invoices.filter((i) => i.status !== 'DRAFT').length} of ${invoices.length} invoices issued`} />
              <Stat label="Deferred / (unbilled)" value={money(billed - recognized, c.currency)} sub={billed >= recognized ? 'Billed ahead of revenue' : 'Revenue ahead of billing'} />
            </div>
            <div className="space-y-5">
              <Card title="Performance obligations" subtitle="Transaction price allocated by relative standalone selling price" pad={false}>
                <Table>
                  <thead>
                    <tr>
                      <Th>Item</Th>
                      <Th>Pattern</Th>
                      <Th>Period</Th>
                      <Th right>Qty</Th>
                      <Th right>Contract price</Th>
                      <Th right>Allocated (SSP)</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id}>
                        <Td>
                          <div className="font-medium">{products.byId.get(it.productId)?.name}</div>
                          <div className="text-[12px] text-muted">{it.description}</div>
                        </Td>
                        <Td>
                          <Badge tone={it.revenuePattern === 'POINT_IN_TIME' ? 'neutral' : 'info'}>{patternLabel(it.revenuePattern)}</Badge>
                        </Td>
                        <Td muted className="whitespace-nowrap">
                          {date(it.startDate, { year: false })} – {date(it.endDate)}
                        </Td>
                        <Td right>{Number(it.quantity).toLocaleString()}</Td>
                        <Td right>{money(it.amount, c.currency)}</Td>
                        <Td right className={it.allocatedAmount !== it.amount ? 'font-medium text-info' : ''}>
                          {money(it.allocatedAmount, c.currency)}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card>
              <Card title="Revenue schedule" subtitle="Posted lines are in the ledger; planned lines post automatically as their dates arrive">
                {periods.length ? (
                  <>
                    <BarChart
                      labels={periods.map((p) => period(p, true))}
                      stacked
                      series={[
                        { name: 'Recognized', values: posted, color: 'var(--color-series-1)' },
                        { name: 'Planned', values: planned, color: 'var(--color-brand-line)' },
                      ]}
                      format={(v) => money(v, c.currency)}
                      axisFormat={(v) => money(v, c.currency, { compact: true })}
                      height={190}
                    />
                    <div className="mt-2">
                      <Legend
                        series={[
                          { name: 'Recognized', color: 'var(--color-series-1)' },
                          { name: 'Planned', color: 'var(--color-brand-line)' },
                        ]}
                      />
                    </div>
                  </>
                ) : (
                  <Empty title="No schedule yet">Activate the contract to build its revenue schedule.</Empty>
                )}
              </Card>
              <Card title="Billing schedule" pad={false}>
                {invoices.length ? (
                  <Table dense>
                    <thead>
                      <tr>
                        <Th>Invoice</Th>
                        <Th>Issue date</Th>
                        <Th>Service period</Th>
                        <Th right>Amount</Th>
                        <Th>Status</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices
                        .slice()
                        .sort((a, b) => a.issueDate.localeCompare(b.issueDate))
                        .map((i) => (
                          <tr key={i.id}>
                            <Td>
                              <Link to={`/ar/invoices/${i.id}`} className="font-medium whitespace-nowrap text-brand hover:underline">
                                {i.number}
                              </Link>
                            </Td>
                            <Td className="num">{date(i.issueDate)}</Td>
                            <Td muted>{i.servicePeriodStart ? `${date(i.servicePeriodStart, { year: false })} – ${date(i.servicePeriodEnd)}` : '—'}</Td>
                            <Td right>{money(i.total, i.currency)}</Td>
                            <Td>
                              <InvoiceStatus i={i} />
                            </Td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                ) : (
                  <Empty title="No invoices yet" />
                )}
              </Card>
            </div>
            <Modal
              open={terminating}
              onClose={() => setTerminating(false)}
              title={`Terminate ${c.number}`}
              footer={
                <>
                  <Button onClick={() => setTerminating(false)}>Cancel</Button>
                  <Button variant="danger" loading={terminate.isPending} onClick={() => terminate.mutate(undefined, { onSuccess: () => setTerminating(false) })}>
                    Terminate contract
                  </Button>
                </>
              }
            >
              <p className="mb-3 text-[13px] text-muted">Planned revenue after this date is cancelled and unissued invoices are dropped. Revenue already recognized stays.</p>
              <Field label="Effective date">
                <Input type="date" value={termDate} onChange={(e) => setTermDate(e.target.value)} />
              </Field>
            </Modal>
          </>
        );
      }}
    </Query>
  );
}

interface DraftItem {
  key: number;
  productId: string;
  description: string;
  quantity: string;
  amount: string;
}
let n = 0;
const blankItem = (): DraftItem => ({ key: ++n, productId: '', description: '', quantity: '1', amount: '' });

export function NewContractPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const customers = useCustomers();
  const products = useProducts();
  const [customerId, setCustomerId] = useState(params.get('customerId') ?? '');
  const [start, setStart] = useState(todayIso().slice(0, 8) + '01');
  const [end, setEnd] = useState('');
  const [freq, setFreq] = useState('ANNUAL');
  const [activate, setActivate] = useState(true);
  const [items, setItems] = useState<DraftItem[]>([blankItem()]);
  const [newFor, setNewFor] = useState<number | null>(null);
  const customer = customers.byId.get(customerId);
  const currency = customer?.currency ?? 'USD';
  const parsed = items.map((i) => ({ ...i, minor: parseAmount(i.amount, currency) }));
  const total = parsed.reduce((s, i) => s + (i.minor ?? 0), 0);
  const termEnd = end || (start ? oneYearLater(start) : '');
  const valid = !!customer && !!start && parsed.every((i) => i.productId && i.minor !== null && i.minor >= 0);
  const create = useAction(
    () =>
      api<{ contract: Contract }>('/api/contracts', {
        body: { customerId, startDate: start, endDate: termEnd, billingFrequency: freq, activate, items: parsed.map((i) => ({ productId: i.productId, description: i.description || undefined, quantity: i.quantity, amount: i.minor })) },
      }),
    { success: activate ? 'Contract created and activated' : 'Draft contract saved' },
  );
  const up = (key: number, patch: Partial<DraftItem>) => setItems((xs) => xs.map((x) => (x.key === key ? { ...x, ...patch } : x)));

  return (
    <>
      <PageHeader
        eyebrow={
          <Link to="/ar/contracts" className="hover:text-ink">
            ← Contracts
          </Link>
        }
        title="New contract"
        description="Enter the price per item as signed. RareMargin allocates the total by standalone selling price and builds revenue and billing schedules on activation."
      />
      <Card>
        <div className="grid gap-4 md:grid-cols-4">
          <Field label="Customer" className="md:col-span-2">
            <Select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
              <option value="">Select customer…</option>
              {customers.list.filter((c) => !c.archivedAt || c.id === customerId).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.currency})
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Billing">
            <Select value={freq} onChange={(e) => setFreq(e.target.value)}>
              {['UPFRONT', 'MONTHLY', 'QUARTERLY', 'ANNUAL'].map((f) => (
                <option key={f} value={f}>
                  {titleCase(f)}
                </option>
              ))}
            </Select>
          </Field>
          <div />
          <Field label="Start date">
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </Field>
          <Field label="End date" hint={!end ? '12-month term by default' : undefined}>
            <Input type="date" value={termEnd} onChange={(e) => setEnd(e.target.value)} />
          </Field>
        </div>
      </Card>
      <Card className="mt-4" pad={false} title="Items">
        <Table>
          <thead>
            <tr>
              <Th w="28%">Product</Th>
              <Th>Description</Th>
              <Th w="90px">Qty</Th>
              <Th w="170px">Price (total for term)</Th>
              <Th w="36px" />
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.key}>
                <Td>
                  <Select value={i.productId} onChange={(e) => (e.target.value === NEW_PRODUCT ? setNewFor(i.key) : up(i.key, { productId: e.target.value }))}>
                    <option value="">{products.list.length || products.isLoading ? 'Select product…' : 'No products yet'}</option>
                    {products.list.filter((p) => !p.archivedAt || p.id === i.productId).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} · {patternLabel(p.revenuePattern)}
                      </option>
                    ))}
                    <option value={NEW_PRODUCT}>+ New product…</option>
                  </Select>
                </Td>
                <Td>
                  <Input value={i.description} onChange={(e) => up(i.key, { description: e.target.value })} />
                </Td>
                <Td>
                  <Input inputMode="decimal" value={i.quantity} onChange={(e) => up(i.key, { quantity: e.target.value })} />
                </Td>
                <Td>
                  <MoneyInput currency={currency} value={i.amount} onChange={(e) => up(i.key, { amount: e.target.value })} placeholder="0.00" />
                </Td>
                <Td>
                  <button onClick={() => setItems((xs) => (xs.length > 1 ? xs.filter((x) => x.key !== i.key) : xs))} className="rounded p-1 text-faint hover:text-bad" aria-label="Remove item">
                    <Icons.Trash size={15} />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <Td colSpan={3} className="border-b-0">
                <Button size="sm" variant="ghost" icon={<Icons.Plus size={14} />} onClick={() => setItems((xs) => [...xs, blankItem()])}>
                  Add item
                </Button>
              </Td>
              <Td right className="border-b-0 text-[14px] font-semibold">
                {money(total, currency)}
              </Td>
              <Td className="border-b-0" />
            </tr>
          </tfoot>
        </Table>
        <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
          <div className="w-96">
            <Toggle checked={activate} onChange={setActivate} label="Activate now" description="Creates the revenue schedule and scheduled invoices; due invoices issue immediately" />
          </div>
          <Button variant="primary" disabled={!valid} loading={create.isPending} onClick={() => create.mutate(undefined, { onSuccess: (r) => navigate(`/ar/contracts/${r.contract.id}`) })}>
            Create contract
          </Button>
        </div>
        {create.error && <ErrorNote error={create.error} className="mx-4 mb-4" />}
      </Card>
      <NewProductModal open={newFor !== null} onClose={() => setNewFor(null)} onCreated={(p) => newFor !== null && up(newFor, { productId: p.id })} />
    </>
  );
}

function oneYearLater(d: string) {
  const t = new Date(`${d}T00:00:00Z`);
  t.setUTCFullYear(t.getUTCFullYear() + 1);
  t.setUTCDate(t.getUTCDate() - 1);
  return t.toISOString().slice(0, 10);
}
